// src/components/personalize/PersonalizeModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, EditPencil, DeleteICon } from '../../public/icons';
import reviewService from '../../services/reviewService';

/* ─────────────────────────────────────────────
   Draft persistence for the modal's in-progress work
   ───────────────────────────────────────────── */
const MODAL_DRAFT_KEY = 'kapta_personalize_modal_draft';

const loadModalDraft = () => {
  try {
    const raw = localStorage.getItem(MODAL_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load modal draft:', err);
    return null;
  }
};

const saveModalDraft = (data) => {
  try {
    localStorage.setItem(MODAL_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save modal draft:', err);
  }
};

const clearModalDraft = () => {
  try {
    localStorage.removeItem(MODAL_DRAFT_KEY);
  } catch (err) {
    console.error('Failed to clear modal draft:', err);
  }
};

const PersonalizeModal = ({ isOpen, onClose, onSave, onProgressChange }) => {
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryStory, setMemoryStory] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [allMagnets, setAllMagnets] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  /* Prevents first-render autosave from clobbering the restored draft */
  const draftHydratedRef = useRef(false);

  /* ─────────────────────────────────────────────
     1. HYDRATE from localStorage when modal opens
     ───────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadModalDraft();
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.memoryTitle === 'string') setMemoryTitle(draft.memoryTitle);
    if (typeof draft.memoryStory === 'string') setMemoryStory(draft.memoryStory);
    if (Array.isArray(draft.uploadedImages)) setUploadedImages(draft.uploadedImages);
    if (Array.isArray(draft.allMagnets)) setAllMagnets(draft.allMagnets);
    if (draft.editingIndex === null || typeof draft.editingIndex === 'number') {
      setEditingIndex(draft.editingIndex);
    }

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ─────────────────────────────────────────────
     2. AUTOSAVE + broadcast to parent on every change
     ───────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = {
      memoryTitle,
      memoryStory,
      uploadedImages,
      allMagnets,
      editingIndex
    };

    saveModalDraft(draft);
    onProgressChange?.(draft);
  }, [
    isOpen,
    memoryTitle,
    memoryStory,
    uploadedImages,
    allMagnets,
    editingIndex,
    onProgressChange
  ]);

  if (!isOpen) return null;

  const hasCurrentContent =
    memoryTitle.trim() !== '' ||
    memoryStory.trim() !== '' ||
    uploadedImages.length > 0;

  /* ───────── Upload to Cloudinary ───────── */
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadError('');
    setIsUploading(true);

    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`${file.name} exceeds 10MB and was skipped.`);
          continue;
        }
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          name: file.name
        });
      }
      setUploadedImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadError('Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setMemoryTitle('');
    setMemoryStory('');
    setUploadedImages([]);
    setUploadError('');
    setEditingIndex(null);
  };

  const handleAddAnother = () => {
    if (!hasCurrentContent) return;

    const magnet = {
      memoryTitle: memoryTitle.trim(),
      memoryStory: memoryStory.trim(),
      uploadedImages: [...uploadedImages]
    };

    setAllMagnets((prev) => {
      if (editingIndex !== null) {
        const next = [...prev];
        next[editingIndex] = magnet;
        return next;
      }
      return [...prev, magnet];
    });

    resetForm();
  };

  const handleEditSavedMagnet = (index) => {
    const magnet = allMagnets[index];
    setMemoryTitle(magnet.memoryTitle);
    setMemoryStory(magnet.memoryStory);
    setUploadedImages(magnet.uploadedImages);
    setEditingIndex(index);

    document
      .getElementById('photo-upload-section')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteSavedMagnet = (index) => {
    setAllMagnets((prev) => prev.filter((_, i) => i !== index));

    if (editingIndex === index) {
      resetForm();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  /* ───────── Final save ─────────
     Commits the in-progress form and clears the modal's own draft.
     From here on, the PARENT owns the state and will autosave it. */
  const handleFinalSave = () => {
    let finalMagnets = [...allMagnets];

    if (hasCurrentContent) {
      const magnet = {
        memoryTitle: memoryTitle.trim(),
        memoryStory: memoryStory.trim(),
        uploadedImages: [...uploadedImages]
      };

      if (editingIndex !== null) {
        finalMagnets[editingIndex] = magnet;
      } else {
        finalMagnets.push(magnet);
      }
    }

    /* 🟢 Clear the modal's local draft — the parent will now save it */
    clearModalDraft();

    onSave(finalMagnets);
    onClose();
  };

  /* ───────── Close without saving ─────────
     Deliberately does NOT clear the draft — so the user can reopen
     and continue where they left off. */
  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={handleClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mx-6 mb-4 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Personalise your Magnets
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                {allMagnets.length > 0
                  ? `You have ${allMagnets.length} magnet${
                      allMagnets.length !== 1 ? 's' : ''
                    } saved`
                  : 'Each magnet holds a photo on the front and a story behind it.'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Saved magnets */}
            {allMagnets.length > 0 && (
              <div className="mb-8">
                <div className="space-y-6">
                  {allMagnets.map((magnet, index) => (
                    <div key={index} className="flex gap-3 mb-6">
                      <div className="overflow-hidden flex-shrink-0">
                        {magnet.uploadedImages.length > 0 ? (
                          <img
                            src={magnet.uploadedImages[0].url}
                            alt={`Magnet ${index + 1}`}
                            className="h-32 w-32 object-cover"
                          />
                        ) : (
                          <img
                            src="https://www.realsimple.com/thmb/1nO0GmEuF87RSxFBmTWtfo6TZW0=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/the-best-gift-giver-GettyImages-1706575747-2089a300e6594496b7f558585a4baefb.jpg"
                            alt="Sample magnet"
                            className="h-32 w-32 object-cover"
                          />
                        )}
                      </div>
                      <div className="flex gap-4 flex-1 min-w-0">
                        <div className="text-left min-w-0 flex-1">
                          <h3 className="text-xl font-bold text-[#1C2A46] mb-0.5 break-words">
                            {magnet.memoryTitle || 'I Love you Mom'}
                          </h3>
                          <p className="text-[#6B635D] text-sm leading-relaxed line-clamp-4">
                            {magnet.memoryStory ||
                              'The Travel Memories collection features 6 carefully selected magnets, each with a beautiful image on the front and a meaningful story on the back. Perfect for decorating your fridge, office space, or as a thoughtful gift for someone special.'}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-2 flex-shrink-0 self-start">
                          <button
                            onClick={() => handleEditSavedMagnet(index)}
                            className="text-secondary font-medium flex items-center gap-2 hover:text-[#C49660] transition-colors text-sm"
                            title="Edit this magnet"
                          >
                            <EditPencil />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDeleteSavedMagnet(index)}
                            className="text-[#C03A2B] font-medium flex items-center gap-2 hover:text-[#A82121] transition-colors text-sm"
                            title="Delete this magnet"
                          >
                            <DeleteICon size={18} color="#C03A2B" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Editing banner */}
            {editingIndex !== null && (
              <div className="mb-5 bg-[#FFF4D6] border-l-4 border-[#D8A100] px-4 py-3 rounded-r">
                <p className="text-sm text-[#8F7F2F] text-left">
                  ✏️ Editing magnet {editingIndex + 1}. Make your changes and click{' '}
                  <strong>Save changes</strong> to update it.
                </p>
              </div>
            )}

            {/* Form */}
            <div
              className="flex flex-col sm:flex-row gap-5 w-full"
              id="photo-upload-section"
            >
              {/* Photo upload */}
              <div className="mb-6 w-full sm:w-1/2">
                <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                  Add Photo
                </h4>
                <div
                  className={`bg-black h-72 w-52 flex flex-col justify-center items-center transition-opacity ${
                    isUploading
                      ? 'cursor-wait'
                      : 'cursor-pointer hover:opacity-90'
                  }`}
                  onClick={() => {
                    if (!isUploading) {
                      document.getElementById('photo-upload').click();
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
                      <p className="text-sm text-white mt-2">Uploading…</p>
                    </>
                  ) : uploadedImages.length > 0 ? (
                    <div className="relative w-full h-full">
                      <img
                        src={uploadedImages[0].url}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                      />
                      <div
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer hover:bg-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(0);
                        }}
                      >
                        ×
                      </div>
                      {uploadedImages.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 text-xs rounded">
                          +{uploadedImages.length - 1} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="#1C2A46"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-white mt-2 text-center px-2">
                        PNG, JPG, JPEG <br />
                        (max size. 10mb)
                      </p>
                    </>
                  )}
                </div>

                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  multiple
                  disabled={isUploading}
                />

                {uploadError && (
                  <p className="mt-2 text-xs text-red-600 text-left">
                    {uploadError}
                  </p>
                )}
              </div>

              {/* Title + Story */}
              <div className="w-full sm:w-1/2">
                <div className="mb-4">
                  <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                    Memory Title (optional)
                  </h4>
                  <input
                    type="text"
                    placeholder='e.g. "For When You Need a Reminder"'
                    value={memoryTitle}
                    onChange={(e) => setMemoryTitle(e.target.value)}
                    className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light"
                  />
                </div>

                <div>
                  <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                    Memory Story (Back of magnet)*
                  </h4>
                  <textarea
                    placeholder="Tell the story behind this moment. who was there? what happened? and why does it matter?"
                    value={memoryStory}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setMemoryStory(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#666666] placeholder:font-light h-40"
                    maxLength="2000"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {memoryStory.length}/2000
                  </div>
                </div>
              </div>
            </div>

            {/* + Add another / Save changes */}
            <div className="flex justify-end items-start mt-4">
              <button
                onClick={handleAddAnother}
                disabled={isUploading || !hasCurrentContent}
                className={`w-40 border py-3 font-medium transition-colors ${
                  isUploading || !hasCurrentContent
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146]'
                }`}
              >
                {editingIndex !== null ? 'Save changes' : '+ Add another'}
              </button>
            </div>
          </div>

          {/* Final save */}
          <div className="p-6">
            <button
              onClick={handleFinalSave}
              disabled={
                (allMagnets.length === 0 && !hasCurrentContent) || isUploading
              }
              className={`w-full py-3 font-medium transition-colors ${
                (allMagnets.length === 0 && !hasCurrentContent) || isUploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
              }`}
            >
              {isUploading
                ? 'Uploading…'
                : allMagnets.length === 0
                ? 'Save my Design'
                : `Save ${allMagnets.length} Magnet${
                    allMagnets.length !== 1 ? 's' : ''
                  }`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalizeModal;